"use client";

/* eslint-disable */
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import PrimeReact from "primereact/api";

import { useHistory, useLocation } from "react-router-dom";

import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";

import { round } from "lodash";
import moment from "moment";

import { nanoid } from "nanoid";
import { InputNumber } from "primereact/inputnumber";

import { useToast } from "../../../../hooks/toast";
import api from "../../../../services/api";
import { totalizarSelecao } from "../../../../utils/hourSelectionTotals";
import { criarBuscaDeValorHora } from "../../../../utils/valorHoraPorReferencia";
import { Container } from "./styles";

interface IBilledHours {
  row_number: number;
  horas_id: number;
  horas_cod_cliente: number;
  horas_cod_referencia: number;
  horas_descricao: string;
  horas_data: Date;
  hora_data?: string;
  horas_hora_inicial: Date;
  horas_hora_final: Date;
  horas_documento: string;
  horas_data_alteracao: Date;
  horas_hora_total: string;
  horas_tipo: string;
  responsavel_nome?: string;
  advogado_nome: string;
  administrador_nome: string;
  cliente_nome?: string;
  valor_ajuste_nao_faturado: number;
  horas_fatu_ajuste: number | null;
  proc_cobrar_por_hora: string;
}

interface IConfirmacaoAjuste {
  hora: IBilledHours;
  acao: "aplicar" | "desfazer";
  minutosLancados: number;
  horasCobrancaAtual: number;
  horasCobrancaNova: number;
  substituiAjusteManual: boolean;
}

interface IReferenceValues {
  horas_cod_referencia: number;
  horas_referencia_nome: string;
  horas_valor_hora: number;
}

// === TIME HELPERS ===
const pad2 = (n: number) => String(n).padStart(2, "0");

// Aceita: "HH:MM", "H:M", "H:7.8" (legado), "0,22" ou "0.22" (horas decimais)
const parseFlexibleToMinutes = (val?: string | null): number => {
  if (!val) return 0;
  const s = String(val).trim();

  // Possui "H:…"
  if (s.includes(":")) {
    const [hStr = "0", mRaw = "0"] = s.split(":");
    const h = Number.parseInt(hStr, 10) || 0;

    let m: number;
    if (mRaw.includes(".") || mRaw.includes(",")) {
      // FORMATO LEGADO "H:7.8"  -> minutos ≈ round( 7.8 * 100 / 60 ) = 13
      const frac = Number.parseFloat(mRaw.replace(",", "."));
      m = Number.isNaN(frac) ? 0 : Math.round((frac * 100) / 60);
    } else {
      m = Number.parseInt(mRaw, 10) || 0;
    }

    // carry se m >= 60
    return h * 60 + m;
  }

  // Horas decimais puras ("0,22"/"0.22")
  const dec = Number.parseFloat(s.replace(",", "."));
  if (!Number.isNaN(dec)) return Math.round(dec * 60);

  return 0;
};

const minutesToHHMM = (total: number): string => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${pad2(h)}:${pad2(m)}`; // horas com zero à esquerda p/ bater com tela
};

// === AJUSTE DE COBRANÇA (50%) ===

/**
 * A mesma conta do servidor, em `routes/ajusteDeCobranca.ts`. Aqui ela serve
 * só para antecipar o número na confirmação — quem grava é o servidor.
 */
const ajusteDeMetade = (minutos: number): number =>
  Math.round((-minutos / 60 / 2) * 100) / 100;

const ajusteDaLinha = (rowData: { valor_ajuste_nao_faturado?: number }): number =>
  Number(rowData.valor_ajuste_nao_faturado ?? 0);

/** Horas que serão cobradas: a duração lançada mais o ajuste. */
const horasDeCobranca = (rowData: {
  horas_hora_total: string;
  valor_ajuste_nao_faturado?: number;
}): number =>
  parseFlexibleToMinutes(rowData.horas_hora_total) / 60 + ajusteDaLinha(rowData);

/**
 * Se o ajuste gravado é exatamente o de 50%, a linha ganha a marca. Dá para
 * saber sem consultar o histórico, porque a conta é determinística.
 */
const temAjusteDeMetade = (rowData: {
  horas_hora_total: string;
  valor_ajuste_nao_faturado?: number;
}): boolean => {
  const minutos = parseFlexibleToMinutes(rowData.horas_hora_total);
  if (minutos <= 0) return false;
  return ajusteDaLinha(rowData) === ajusteDeMetade(minutos);
};

const ehHoraFaturada = (rowData: { horas_documento?: string }): boolean =>
  !!rowData.horas_documento && rowData.horas_documento.trim().length > 1;

const HourManagementReport: React.FC = () => {
  PrimeReact.ripple = true;

  const toast = useRef<Toast | null>(null);
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const state = location.state as any;

  let initialSearchDate = "";
  let finalSearchDate = "";
  if (state) {
    initialSearchDate = moment(state?.proc_data_ajuiz_inicio).format(
      "DD/MM/YYYY"
    );
    finalSearchDate = moment(state?.proc_data_ajuiz_final).format("DD/MM/YYYY");
    if (
      state.dateFilterType === "periodo" &&
      state?.proc_data_ajuiz_final === undefined
    ) {
      finalSearchDate = moment(state?.proc_data_ajuiz_inicio)
        .clone()
        .endOf("month")
        .format("DD/MM/YYYY");
    }
  }

  const emptyBilledHour = {
    row_number: 0,
    horas_id: 0,
    horas_cod_cliente: 0,
    horas_cod_referencia: 0,
    horas_descricao: "",
    horas_data: new Date(),
    horas_hora_inicial: new Date(),
    horas_hora_final: new Date(),
    horas_documento: "",
    horas_data_alteracao: new Date(),
    horas_hora_total: "",
    horas_tipo: "",
  };

  localStorage.removeItem("@Jurisnet:ajustevalores");
  localStorage.removeItem("@Jurisnet:valorajuste");
  localStorage.removeItem("@Jurisnet:invoicestart");
  localStorage.removeItem("@Jurisnet:invoiceend");
  localStorage.removeItem("@Jurisnet:invoice");
  const tempSelectedHour = localStorage.getItem("@Jurisnet:selectedhour");

  const [billedHours, setBilledHours] = useState<IBilledHours[]>([]);
  const [billedHour, setBilledHour] = useState(emptyBilledHour);
  const [selectedHours, setSelectedHours] = useState<IBilledHours[]>([]);
  const [deleteHourDialog, setDeleteHourDialog] = useState(false);

  const [totalTime, setTotalTime] = useState("");
  const [totalDecimalTime, setTotalDecimalTime] = useState("");

  const [valuePerHour, setValuePerHour] = useState("0.00");
  const [totalValuePeriod, setTotalValuePeriod] = useState("0.00");
  const [totalAddedValuePeriod, setTotalAddedValuePeriod] = useState("0.00");

  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const dt = useRef(null);

  const [visibleChangeClient, setVisibleChangeClient] = useState(false);
  const [changeClientHour, setChangeClientHour] = useState<IBilledHours | null>(
    null
  );
  const [customersOptions, setCustomersOptions] = useState<
    { name: string; value: number }[]
  >([]);
  const [newClientId, setNewClientId] = useState<number>(0);
  const [applyToAllSelected, setApplyToAllSelected] = useState(false);

  const globalReferenceId = useRef(0);
  const globalCategoryValue = useRef(0);
  const adjustment = useRef("");
  const enteredAdjustmentValue = useRef(0.0);
  const [valorHoraEfetivo, setValorHoraEfetivo] = useState(0);
  const [totalDecimalCobranca, setTotalDecimalCobranca] = useState("");
  const [recarga, setRecarga] = useState(0);
  const [ajusteEmCurso, setAjusteEmCurso] = useState(false);
  const [confirmacaoAjuste, setConfirmacaoAjuste] =
    useState<IConfirmacaoAjuste | null>(null);
  const [adjustmentInputValue, setAdjustmentInputValue] = useState(0);
  const skipAdjustmentEffect = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const options: { name: string; value: number }[] = [];
        await api.get("customers/show/all").then((response) => {
          response.data.forEach(
            (customer: { cliente_id: number; cliente_nome: string }) => {
              options.push({
                name: customer.cliente_nome,
                value: customer.cliente_id,
              });
            }
          );
        });
        setCustomersOptions(options);
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
      }
    })();
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("@Jurisnet:selectedhour");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const hasOnlyNullAdjustments = parsed.every(
            (hour) => hour.horas_fatu_ajuste === null
          );

          if (hasOnlyNullAdjustments) {
            skipAdjustmentEffect.current = true;
            enteredAdjustmentValue.current = 0;
            setAdjustmentInputValue(0);
          }
        }
      } catch (e) {
        console.error("Erro ao analisar horas selecionadas:", e);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        localStorage.setItem("@Jurisnet:invoice", "false");
        setLoading(true);
        await api.post("/reports/hour/general/desc", state).then((response) => {
          setBilledHours(response.data);

          const durations: number[] = [];
          let valor_categoria = 0.0;
          let total_do_periodo = 0.0;
          let nonBilledValue = 0.0;
          let horas_decimal_total = 0.0;
          let total_ajuste_decimal = 0.0;

          let ind = 0;
          const valorDaHora = criarBuscaDeValorHora((id) =>
            api.post(`/reports/hour/general/referencevalues/false/${id}`, state)
          );

          response.data.forEach(async (data: IBilledHours) => {
            const referenceId = data.horas_cod_referencia
              ? data.horas_cod_referencia
              : 0;
            globalReferenceId.current = referenceId;
            nonBilledValue = data.valor_ajuste_nao_faturado
              ? data.valor_ajuste_nao_faturado
              : 0;

            total_ajuste_decimal += Number(nonBilledValue);

            const minutes = parseFlexibleToMinutes(
              data.horas_hora_total || "0:00"
            );
            durations.push(minutes);
            const horasDecimal = minutes / 60; // decimal correto
            horas_decimal_total += horasDecimal;

            // general info and index adjustments
            // busca o valor individual cobrado pela HORA
            await valorDaHora(referenceId)
              .then((responseRef) => {
                const referenceHourValueItem = localStorage.getItem(
                  "@Jurisnet:referenceValue"
                );

                valor_categoria = Number(responseRef.data[0]?.horas_valor_hora ?? 0);
                globalCategoryValue.current = Number(
                  responseRef.data[0]?.horas_valor_hora
                );

                let referenceValue = "";
                if (referenceHourValueItem) {
                  referenceValue = JSON.parse(referenceHourValueItem);
                  if (
                    Number(responseRef.data[0]?.horas_valor_hora ?? 0) !==
                    Number(referenceValue)
                  ) {
                    valor_categoria = Number(referenceValue);
                  }
                }
              });

            setValorHoraEfetivo((anterior) =>
              anterior === valor_categoria ? anterior : valor_categoria
            );

            total_do_periodo += round(
              (Number(nonBilledValue) + horasDecimal) * valor_categoria,
              2
            );

            if (ind === response.data.length - 1) {
              const newPeriodValue =
                Number(round(total_do_periodo, 2)) +
                Number(adjustment.current.replace(",", ".")) *
                  Number(globalCategoryValue.current);
              const formattedPeriod = newPeriodValue?.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              const formattedCategoryValue = valor_categoria?.toLocaleString(
                "pt-BR",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              );
              const formattedtotalDecimalTime =
                horas_decimal_total?.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              const formattedNonBilledValue =
                total_ajuste_decimal?.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });

              const newTotalValue =
                Number(round(newPeriodValue, 2)) +
                Number(enteredAdjustmentValue.current);

              const formattedAddedTotalValue = newTotalValue?.toLocaleString(
                "pt-BR",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              );

              adjustment.current = formattedNonBilledValue;
              if (formattedNonBilledValue === "0,00") {
                adjustment.current = "";
              }

              const cobrancaDecimal =
                horas_decimal_total + Number(total_ajuste_decimal);
              setTotalDecimalCobranca(
                cobrancaDecimal.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              );
              setValuePerHour(formattedCategoryValue);
              setTotalValuePeriod(formattedPeriod);
              setTotalDecimalTime(formattedtotalDecimalTime);
              setTotalAddedValuePeriod(formattedAddedTotalValue);
            }
            ind += 1;
          });

          const rawStorage = localStorage.getItem("@Jurisnet:valorajuste");
          const parsedStorage = rawStorage ? JSON.parse(rawStorage) : null;

          const ajusteFromStorage =
            parsedStorage !== null &&
            !["0", "0.00", 0].includes(parsedStorage) &&
            !isNaN(Number(parsedStorage))
              ? Number(parsedStorage)
              : null;

          const ajusteFromAPI = response.data[0]?.horas_fatu_ajuste;

          if (!skipAdjustmentEffect.current) {
            if (
              ajusteFromStorage !== null &&
              !isNaN(ajusteFromStorage) &&
              ajusteFromStorage !== 0
            ) {
              enteredAdjustmentValue.current = ajusteFromStorage;
              setAdjustmentInputValue(ajusteFromStorage);
            } else if (
              ajusteFromAPI !== null &&
              !isNaN(Number(ajusteFromAPI)) &&
              Number(ajusteFromAPI) !== 0
            ) {
              enteredAdjustmentValue.current = Number(ajusteFromAPI);
              setAdjustmentInputValue(Number(ajusteFromAPI));
            } else {
              enteredAdjustmentValue.current = 0;
              setAdjustmentInputValue(0);
            }
          }

          // Sempre resetar a flag após o uso
          skipAdjustmentEffect.current = false;

          const totalMinutes = durations.reduce((acc, v) => acc + v, 0);
          setTotalTime(minutesToHHMM(totalMinutes));
        });

        if (tempSelectedHour) {
          let searchHours: IBilledHours[] = [];
          searchHours = JSON.parse(tempSelectedHour);
          setSelectedHours(searchHours);
        }

        setLoading(false);
      } catch (err) {
        addToast({
          type: "info",
          title: "Sem resultados!",
          description:
            "A pesquisa não apresentou resultados. Listando todos os cadastros no sistema.",
        });
        setLoading(false);
      }
    })();
    // `recarga` refaz a consulta sem recarregar a página: os totais do resumo
    // voltam a ser calculados pelo mesmo caminho da carga inicial, e o estado
    // da tela — a seleção, principalmente — sobrevive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, tempSelectedHour, recarga]);

  // Depois de recarregar os dados, a seleção precisa apontar para as linhas
  // novas. Sem isto o rodapé somaria valores velhos, e foi o que deixava a
  // tela confusa: havia hora selecionada, mas com os números de antes.
  useEffect(() => {
    // Sem dados ainda não há o que remapear: a seleção restaurada do
    // localStorage precisa sobreviver até a consulta voltar.
    if (billedHours.length === 0) return;

    setSelectedHours(anterior => {
      if (anterior.length === 0) return anterior;

      const porId = new Map(billedHours.map(linha => [linha.horas_id, linha]));

      const atualizada = anterior
        .map(linha => porId.get(linha.horas_id))
        .filter((linha): linha is IBilledHours => !!linha);

      const mudou =
        atualizada.length !== anterior.length ||
        atualizada.some((linha, i) => linha !== anterior[i]);

      return mudou ? atualizada : anterior;
    });
  }, [billedHours]);

  useEffect(() => {
    const checkAndClearAdjustment = async () => {
      if (selectedHours.length > 0) {
        const shouldCheckDB = !localStorage.getItem("@Jurisnet:valorajuste");

        if (shouldCheckDB) {
          await refreshSelectedHoursFromAPI();
        }
      }
    };

    checkAndClearAdjustment();
  }, []);

  const hidedeleteHourDialog = () => {
    setDeleteHourDialog(false);
  };

  const editHour = (rowData: { horas_id: number }) => {
    history.push(`/management/workinghour/edit/${rowData.horas_id}`);
    localStorage.setItem("@Jurisnet:hourreporttab", "1");
    localStorage.setItem("@Jurisnet:ajustevalores", "true");
  };

  const openChangeClient = (rowData: IBilledHours) => {
    setChangeClientHour(rowData);
    setNewClientId(rowData.horas_cod_cliente || 0);
    setApplyToAllSelected(false);
    setVisibleChangeClient(true);
  };

  const hideChangeClient = () => {
    setVisibleChangeClient(false);
    setChangeClientHour(null);
    setNewClientId(0);
    setApplyToAllSelected(false);
  };

  const saveChangeClient = async () => {
    if (!changeClientHour) {
      return;
    }

    if (!newClientId || Number(newClientId) <= 0) {
      addToast({
        type: "error",
        title: "Selecione uma empresa",
        description:
          "Selecione a empresa para a qual deseja transferir o(s) registro(s).",
      });
      return;
    }

    const applyToAll =
      applyToAllSelected &&
      selectedHours.length > 1 &&
      selectedHours.some((item) => item.horas_id === changeClientHour.horas_id);

    const targetHours = applyToAll ? selectedHours : [changeClientHour];

    let selectedHoursValues = "";
    targetHours.forEach((item) => {
      selectedHoursValues +=
        selectedHoursValues === ""
          ? `(${item.horas_id})`
          : `,(${item.horas_id})`;
    });

    const formData = {
      horas_cod_cliente: newClientId,
      selectedHours: selectedHoursValues,
    };

    try {
      await api.put("/management/workinghours/changeclient", formData);

      const newClientName =
        customersOptions.find((option) => option.value === Number(newClientId))
          ?.name || "";
      const targetIds = targetHours.map((item) => item.horas_id);

      const applyChange = (item: IBilledHours) =>
        targetIds.includes(item.horas_id)
          ? {
              ...item,
              horas_cod_cliente: Number(newClientId),
              cliente_nome: newClientName,
            }
          : item;

      setBilledHours((prev) => prev.map(applyChange));
      setSelectedHours((prev) => prev.map(applyChange));

      addToast({
        type: "success",
        title: "Empresa atualizada",
        description:
          targetHours.length > 1
            ? `A empresa foi atualizada para ${targetHours.length} registros selecionados.`
            : "A empresa associada ao registro foi atualizada.",
      });

      hideChangeClient();
    } catch (error) {
      addToast({
        type: "error",
        title: "Ocorreu um problema",
        description:
          "Não foi possível atualizar a empresa. Tente novamente ou contate o administrador.",
      });
    }
  };

  const deleteHour = async () => {
    api.delete(`/management/workinghours/delete/${billedHour.horas_id}`);

    setDeleteHourDialog(false);

    const updatedRecords = billedHours.filter(
      (val) => val.horas_id !== billedHour.horas_id
    );
    setBilledHours(updatedRecords);

    addToast({
      type: "success",
      title: "Cadastro excluído!",
      description: "O cadastro selecionado foi excluído com sucesso!",
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const confirmDeleteHour = (
    billedHour: React.SetStateAction<{
      row_number: number;
      horas_id: number;
      horas_cod_cliente: number;
      horas_cod_referencia: number;
      horas_descricao: string;
      horas_data: Date;
      horas_hora_inicial: Date;
      horas_hora_final: Date;
      horas_documento: string;
      horas_data_alteracao: Date;
      horas_hora_total: string;
      horas_tipo: string;
    }>
  ) => {
    setBilledHour(billedHour);
    setDeleteHourDialog(true);
  };

  const deleteHourFooter = (
    <>
      <Button
        type="button"
        label="Não"
        icon="pi pi-times"
        className="p-button-text"
        onClick={hidedeleteHourDialog}
      />
      <Button
        type="button"
        label="Sim"
        icon="pi pi-check"
        className="p-button-text"
        onClick={deleteHour}
      />
    </>
  );

  // O botão de 50% só aparece para administradores. A guarda de verdade está
  // no servidor (`ensureAdministrador`); isto aqui é só a tela.
  const ehAdministrador = useMemo(() => {
    const dados = localStorage.getItem("@Jurisnet:access");
    if (typeof dados !== "string") return false;
    try {
      return JSON.parse(dados).nivelusuario === "Administrador";
    } catch {
      return false;
    }
  }, []);

  const abrirConfirmacaoAjuste = (rowData: IBilledHours) => {
    const minutosLancados = parseFlexibleToMinutes(rowData.horas_hora_total);
    const ajusteAtual = ajusteDaLinha(rowData);
    const ajustada = temAjusteDeMetade(rowData);

    setConfirmacaoAjuste({
      hora: rowData,
      acao: ajustada ? "desfazer" : "aplicar",
      minutosLancados,
      horasCobrancaAtual: minutosLancados / 60 + ajusteAtual,
      horasCobrancaNova: ajustada
        ? minutosLancados / 60
        : minutosLancados / 60 + ajusteDeMetade(minutosLancados),
      substituiAjusteManual: !ajustada && ajusteAtual !== 0,
    });
  };

  const confirmarAjuste = async () => {
    if (!confirmacaoAjuste) return;

    setAjusteEmCurso(true);
    try {
      await api.post(
        `/management/workinghours/${confirmacaoAjuste.hora.horas_id}/ajuste-cobranca`,
        { acao: confirmacaoAjuste.acao }
      );

      const eraDesfazer = confirmacaoAjuste.acao === "desfazer";

      setConfirmacaoAjuste(null);
      setAjusteEmCurso(false);

      // Sem recarregar a página: `recarga` refaz só a consulta, e a seleção
      // continua marcada e com os números novos.
      setRecarga(n => n + 1);

      addToast({
        type: "success",
        title: eraDesfazer ? "Ajuste removido" : "Cobrança reduzida",
        description: eraDesfazer
          ? "A cobrança desta hora voltou ao valor cheio."
          : "A cobrança desta hora passou a 50%. A hora do advogado não mudou.",
      });
    } catch (erro) {
      const mensagem =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (erro as any)?.response?.data?.message ||
        "Não foi possível ajustar a cobrança desta hora.";

      addToast({
        type: "error",
        title: "Ajuste não aplicado",
        description: mensagem,
      });
      setAjusteEmCurso(false);
      setConfirmacaoAjuste(null);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actionBodyTemplate = (rowData: any) => {
    if (rowData.proc_cobrar_por_hora === "N") {
      return (
        <span style={{ color: "red" }}>Cobrança por hora não autorizada</span>
      );
    }
    return (
      <>
        <Button
          type="button"
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-plain"
          onClick={() => {
            editHour(rowData);
          }}
          tooltip="Alterar cadastro"
          tooltipOptions={{ position: "left" }}
        />
        <Button
          type="button"
          icon="pi pi-building"
          className="p-button-rounded p-button-text p-button-plain"
          onClick={() => {
            openChangeClient(rowData);
          }}
          tooltip="Alterar empresa"
          tooltipOptions={{ position: "left" }}
        />
        <Button
          type="button"
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-plain"
          onClick={() => {
            confirmDeleteHour(rowData);
          }}
          tooltip="Excluir cadastro"
          tooltipOptions={{ className: "red-tooltip", position: "left" }}
        />
        <Button
          type="button"
          icon="pi pi-check-circle"
          className="p-button-rounded p-button-text p-button-plain"
          onClick={() => editHour(rowData)}
          tooltip="Ajustar valores"
          tooltipOptions={{ position: "left" }}
          label={rowData.billed}
        />
        {ehAdministrador ? (
          <Button
            type="button"
            icon="pi pi-percentage"
            className={
              temAjusteDeMetade(rowData)
                ? "p-button-rounded p-button-text p-button-warning"
                : "p-button-rounded p-button-text p-button-plain"
            }
            disabled={
              ehHoraFaturada(rowData) ||
              parseFlexibleToMinutes(rowData.horas_hora_total) <= 0
            }
            onClick={() => abrirConfirmacaoAjuste(rowData)}
            tooltip={
              ehHoraFaturada(rowData)
                ? "Hora já faturada — cancele o faturamento para ajustar"
                : parseFlexibleToMinutes(rowData.horas_hora_total) <= 0
                ? "Lançamento sem duração válida"
                : temAjusteDeMetade(rowData)
                ? "Remover o ajuste de 50%"
                : "Reduzir a cobrança para 50%"
            }
            tooltipOptions={{ position: "left" }}
          />
        ) : null}
        {rowData.horas_documento && rowData.horas_documento.length > 1 ? (
          <Button
            type="button"
            icon="pi pi-money-bill"
            className="p-button-rounded p-button-text p-button-plain p-button-success"
            tooltip="Hora já faturada"
            tooltipOptions={{ position: "left" }}
          />
        ) : (
          <Button
            type="button"
            icon="pi pi-money-bill"
            className="p-button-rounded p-button-text p-button-plain p-button-danger"
            tooltip="Hora NÃO faturada"
            tooltipOptions={{ position: "left" }}
          />
        )}
      </>
    );
  };

  const header = (
    <div className="flex align-items-center export-buttons">
      <span className="p-input-icon-left p-ml-auto">
        <i className="pi pi-search" />
        <InputText
          type="search"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Pesquisar na tabela"
        />
      </span>
    </div>
  );

  const paginatorLeft = (
    <Button type="button" icon="pi pi-refresh" className="p-button-text" />
  );
  const paginatorRight = (
    <Button type="button" icon="pi pi-cloud" className="p-button-text" />
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const counterBodyTemplate = (rowData: any, props: any) => props.rowIndex + 1;

  const hourBodyTemplate = (rowData: { horas_hora_total: string }) => {
    const minutes = parseFlexibleToMinutes(rowData.horas_hora_total);
    return minutesToHHMM(minutes);
  };

  const decimalBodyTemplate = (rowData: { horas_hora_total: string }) => {
    const minutes = parseFlexibleToMinutes(rowData.horas_hora_total);
    return (minutes / 60).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      minimumIntegerDigits: 1,
    });
  };

  const cobrancaBodyTemplate = (rowData: IBilledHours) => {
    const minutos = parseFlexibleToMinutes(rowData.horas_hora_total);
    if (minutos <= 0) return minutesToHHMM(minutos);

    const horas = horasDeCobranca(rowData);
    const ajustada = ajusteDaLinha(rowData) !== 0;

    if (!ajustada) return minutesToHHMM(minutos);

    return (
      <span className="cobranca-ajustada">
        {minutesToHHMM(Math.round(horas * 60))}
        {temAjusteDeMetade(rowData) ? <em>50%</em> : <em>ajuste</em>}
      </span>
    );
  };

  const totalDecimalBodyTemplate = (rowData: {
    valor_ajuste_nao_faturado: number;
  }) => {
    const newValue = rowData.valor_ajuste_nao_faturado
      ?.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        minimumIntegerDigits: 1,
      })
      .replace(".", ",");
    return newValue;
  };

  const checkboxBodyTemplate = (rowData: {
    horas_id: number;
    proc_cobrar_por_hora: string;
  }) => {
    if (billedHours !== undefined) {
      if (rowData.proc_cobrar_por_hora === "N") {
        return "Nao";
      } else {
        return (
          <Checkbox
            inputId={`${rowData.horas_id.toString()}`}
            name={`cb${rowData.horas_id.toString()}`}
            value={selectedHours}
            checked={selectedHours.some(
              linha => linha.horas_id === rowData.horas_id
            )}
          />
        );
      }
    }
  };

  const personInChargeBodyTemplate = (rowData: {
    responsavel_nome?: string;
    advogado_nome: string;
    administrador_nome: string;
  }) =>
    rowData.responsavel_nome ||
    rowData.advogado_nome ||
    rowData.administrador_nome ||
    "";

  const selectionTotals = useMemo(
    () =>
      totalizarSelecao(
        selectedHours.map((row) => ({
          minutos: parseFlexibleToMinutes(row.horas_hora_total),
          valorHora: valorHoraEfetivo,
          ajusteEmHoras: row.valor_ajuste_nao_faturado,
        }))
      ),
    [selectedHours, valorHoraEfetivo]
  );

  const semValorDefinido =
    selectionTotals.linhasSemValor === selectionTotals.quantidade;

  const mutedFooter = { fontWeight: "normal" as const, opacity: 0.75 };

  const hasAdjustmentColumn =
    adjustment.current.length > 0 && adjustment.current !== "0,00";

  const footerGroup = (
    <ColumnGroup>
      {selectionTotals.quantidade > 0 ? (
        <Row>
          <Column
            footer={`Horas selecionadas (${selectionTotals.quantidade}):`}
            colSpan={7}
            footerStyle={{ textAlign: "right" }}
          />
          <Column
            footer={minutesToHHMM(selectionTotals.minutos)}
            footerStyle={{ textAlign: "center" }}
          />
          <Column
            footer={selectionTotals.decimalNaTela}
            footerStyle={{ textAlign: "center" }}
          />
          <Column
            footer={minutesToHHMM(selectionTotals.minutosCobranca)}
            footerStyle={{ textAlign: "center" }}
          />
          {hasAdjustmentColumn ? <Column /> : null}
          <Column
            footer={
              semValorDefinido
                ? "valor da hora não definido"
                : selectionTotals.valorFormatado
            }
            colSpan={2}
            footerStyle={
              semValorDefinido
                ? { textAlign: "center", ...mutedFooter }
                : { textAlign: "center" }
            }
          />
        </Row>
      ) : null}
      {selectionTotals.quantidade > 0 && selectionTotals.divergem ? (
        <Row>
          <Column
            footer="Sem arredondar cada linha:"
            colSpan={7}
            footerStyle={{ textAlign: "right", ...mutedFooter }}
          />
          <Column />
          <Column
            footer={selectionTotals.decimalExato}
            footerStyle={{ textAlign: "center", ...mutedFooter }}
          />
          <Column />
          {hasAdjustmentColumn ? <Column /> : null}
          <Column colSpan={2} />
        </Row>
      ) : null}
      <Row>
        <Column
          footer="Total de horas:"
          colSpan={7}
          footerStyle={{ textAlign: "right" }}
        />
        <Column footer={totalTime} footerStyle={{ textAlign: "center" }} />
        <Column
          footer={totalDecimalTime}
          footerStyle={{ textAlign: "center" }}
        />
        <Column
          footer={totalDecimalCobranca}
          footerStyle={{ textAlign: "center" }}
        />
        {hasAdjustmentColumn ? (
          <Column
            footer={adjustment.current}
            footerStyle={{ textAlign: "center" }}
          />
        ) : null}
        <Column colSpan={2} />
      </Row>
    </ColumnGroup>
  );

  const printSelectedHours = () => {
    if (!selectedHours || selectedHours.length === 0) {
      addToast({
        type: "error",
        title: "Nenhuma hora selecionada!",
        description:
          "Por favor, selecione pelo menos 1 registro para enviar as informações!.",
      });
      return;
    }

    localStorage.setItem(
      "@Jurisnet:selectedhour",
      JSON.stringify(selectedHours)
    );

    if (
      enteredAdjustmentValue.current &&
      enteredAdjustmentValue.current !== 0
    ) {
      localStorage.setItem(
        "@Jurisnet:valorajuste",
        JSON.stringify(enteredAdjustmentValue.current.toFixed(2))
      );
    } else {
      localStorage.removeItem("@Jurisnet:valorajuste");
    }

    localStorage.setItem("@Jurisnet:valorhora", JSON.stringify(valuePerHour));
    localStorage.setItem("@Jurisnet:hourreporttab", "1");

    history.push({
      pathname: "/report/hour/general/selectedhour",
      state,
    });
  };

  const invoiceHours = () => {
    if (!selectedHours) {
      addToast({
        type: "error",
        title: "Nenhuma hora selecionada!",
        description:
          "Por favor, selecione pelo menos 1 registro para enviar as informações!.",
      });
    } else {
      localStorage.setItem("@Jurisnet:invoice", "true");
      localStorage.setItem(
        "@Jurisnet:valorajuste",
        JSON.stringify(enteredAdjustmentValue.current?.toFixed(2) || "0.00")
      );
      localStorage.setItem(
        "@Jurisnet:invoicestart",
        JSON.stringify(initialSearchDate)
      );
      localStorage.setItem(
        "@Jurisnet:invoiceend",
        JSON.stringify(finalSearchDate)
      );
      localStorage.setItem(
        "@Jurisnet:selectedhour",
        JSON.stringify(selectedHours)
      );

      applyAdjustmentValue();

      history.push({
        pathname: "/report/hour/general/selectedhour",
        state,
      });
    }
  };

  // Improve the refreshSelectedHoursFromAPI function to ensure it properly handles null adjustment values
  const refreshSelectedHoursFromAPI = async () => {
    if (selectedHours.length === 0) return;

    const horasIds = selectedHours.map((h) => h.horas_id);

    try {
      const response = await api.post("/management/workinghours/searchByIds", {
        horas_ids: horasIds,
      });

      if (response.data && response.data.length > 0) {
        // Update the selected hours state
        setSelectedHours(response.data);
        localStorage.setItem(
          "@Jurisnet:selectedhour",
          JSON.stringify(response.data)
        );

        // Check if adjustment is null or 0 in the database
        const dbAdjustment: Number | null = response.data[0]?.horas_fatu_ajuste;

        if (dbAdjustment === null || dbAdjustment === 0) {
          enteredAdjustmentValue.current = 0.0;
          setAdjustmentInputValue(0.0);
          localStorage.removeItem("@Jurisnet:valorajuste");
        } else {
          enteredAdjustmentValue.current = Number(dbAdjustment);
          setAdjustmentInputValue(Number(dbAdjustment));
          localStorage.setItem(
            "@Jurisnet:valorajuste",
            JSON.stringify(dbAdjustment.toFixed(2))
          );
        }

        // Recalculate total value with the correct adjustment
        const parsedTotalValue = Number.parseFloat(
          totalValuePeriod.replace(/\./g, "").replace(",", ".")
        );
        const safeTotalValue = isNaN(parsedTotalValue) ? 0 : parsedTotalValue;

        const parsedAdjustment = Number.parseFloat(
          (adjustment.current || "0").replace(",", ".")
        );
        const safeAdjustment = isNaN(parsedAdjustment) ? 0 : parsedAdjustment;

        const categoryValue = Number(globalCategoryValue.current || 0);

        const newTotalValue =
          safeTotalValue +
          enteredAdjustmentValue.current +
          safeAdjustment * categoryValue;

        const newTotalValueString = newTotalValue.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          minimumIntegerDigits: 1,
        });

        setTotalAddedValuePeriod(newTotalValueString);
      }
    } catch (error) {
      console.error("Error refreshing selected hours:", error);
    }
  };

  const applyAdjustmentValue = async () => {
    if (enteredAdjustmentValue.current !== 0) {
      let billedHoursIds = "";

      if (billedHours.length > 0) {
        billedHours.forEach((item: IBilledHours) => {
          if (billedHoursIds === "") {
            billedHoursIds = `(${item.horas_id})`;
          } else {
            billedHoursIds += `,(${item.horas_id})`;
          }
        });
      }

      const isZero = Number(enteredAdjustmentValue.current) === 0;

      const formData = {
        enteredAdjustmentValue: isZero ? null : enteredAdjustmentValue.current,
        apply: !isZero,
        selectedHours: billedHoursIds,
      };

      await api
        .put("/management/workinghours/patch/adjustmentvalue", formData)
        .then(() => {
          const parsedTotalValue = Number.parseFloat(
            totalValuePeriod.replace(/\./g, "").replace(",", ".")
          );
          const safeTotalValue = isNaN(parsedTotalValue) ? 0 : parsedTotalValue;

          const adjustmentValue =
            typeof enteredAdjustmentValue.current === "number"
              ? enteredAdjustmentValue.current
              : 0;

          const parsedAdjustment = Number.parseFloat(
            (adjustment.current || "0").replace(",", ".")
          );
          const safeAdjustment = isNaN(parsedAdjustment) ? 0 : parsedAdjustment;

          const categoryValue = Number(globalCategoryValue.current || 0);

          const newTotalValue =
            round(safeTotalValue, 2) +
            adjustmentValue +
            safeAdjustment * categoryValue;

          const newTotalValueString = newTotalValue.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            minimumIntegerDigits: 1,
          });

          setTotalAddedValuePeriod(newTotalValueString);

          addToast({
            type: "success",
            title: "Valor aplicado",
            description: "O valor do ajuste financeiro foi aplicado.",
          });

          toast.current?.show({
            severity: "success",
            summary: "Valor aplicado",
            detail: "O valor do ajuste financeiro foi aplicado.",
            life: 3000,
          });
        });

      await refreshSelectedHoursFromAPI();
    } else {
      addToast({
        type: "error",
        title: "Nenhum valor informado",
        description: "Informe um valor para aplicar o ajuste financeiro.",
      });
      toast.current?.show({
        severity: "error",
        summary: "Nenhum valor informado",
        detail: "Informe um valor para aplicar o ajuste financeiro.",
        life: 3000,
      });
    }
  };

  const cancelAdjustmentValue = async () => {
    let billedHoursIds = "";

    if (selectedHours.length > 0) {
      selectedHours.forEach((item: IBilledHours) => {
        if (billedHoursIds === "") {
          billedHoursIds = `(${item.horas_id})`;
        } else {
          billedHoursIds += `,(${item.horas_id})`;
        }
      });
    }

    const formData = {
      enteredAdjustmentValue: null,
      apply: false,
      selectedHours: billedHoursIds,
    };

    try {
      await api
        .put("/management/workinghours/patch/adjustmentvalue", formData)
        .then(async () => {
          // Explicitly set adjustment value to zero
          enteredAdjustmentValue.current = 0;
          setAdjustmentInputValue(0);

          // Clear all related localStorage items to prevent persistence
          localStorage.removeItem("@Jurisnet:valorajuste");
          localStorage.setItem("@Jurisnet:valorajuste", JSON.stringify("0.00"));
          // localStorage.removeItem("@Jurisnet:ajustevalores");

          // Update the selected hours in localStorage with the adjustment set to null
          // In the cancelAdjustmentValue function, update the updatedSelectedHours mapping to use 0 instead of null
          if (selectedHours.length > 0) {
            const updatedSelectedHours = selectedHours.map((hour) => ({
              ...hour,
              horas_fatu_ajuste: 0, // Use 0 instead of null to avoid type issues
            }));

            setSelectedHours(updatedSelectedHours);
            localStorage.setItem(
              "@Jurisnet:selectedhour",
              JSON.stringify(updatedSelectedHours)
            );
          }

          // Update the total value display
          const parsedTotalValue = Number.parseFloat(
            totalValuePeriod.replace(/\./g, "").replace(",", ".")
          );
          const safeTotalValue = isNaN(parsedTotalValue) ? 0 : parsedTotalValue;

          const parsedAdjustment = Number.parseFloat(
            (adjustment.current || "0").replace(",", ".")
          );
          const safeAdjustment = isNaN(parsedAdjustment) ? 0 : parsedAdjustment;

          const categoryValue = Number(globalCategoryValue.current || 0);

          // Calculate with zero adjustment value
          const newTotalValue = safeTotalValue + safeAdjustment * categoryValue;

          const newTotalValueString = newTotalValue.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            minimumIntegerDigits: 1,
          });

          setTotalAddedValuePeriod(newTotalValueString);

          addToast({
            type: "success",
            title: "Valor removido",
            description: "O valor do ajuste financeiro foi removido.",
          });

          toast.current?.show({
            severity: "success",
            summary: "Valor removido",
            detail: "O valor do ajuste financeiro foi removido.",
            life: 3000,
          });

          skipAdjustmentEffect.current = true;
          // Force a refresh of the data from the API to ensure we have the latest values
          await refreshSelectedHoursFromAPI();
        });
    } catch (error) {
      console.error("Error canceling adjustment value:", error);
    }
  };

  const [referenceValues, setReferenceValues] = useState<IReferenceValues[]>(
    []
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [referenceValue, setReferenceValue] = useState(0.0);

  useEffect(() => {
    (async () => {
      try {
        await api
          .post("/reports/hour/general/referencevalues/false/0", state)
          .then((response) => {
            setReferenceValues(response.data);
            localStorage.setItem(
              "@Jurisnet:valorhora",
              JSON.stringify(response.data[0]?.horas_valor_hora)
            );
          });
      } catch (err) {
        addToast({
          type: "info",
          title: "Sem resultados!",
          description:
            "A pesquisa não apresentou resultados. Listando todos os cadastros no sistema.",
        });
      }
    })();
  }, [state]);

  let documentNumber = "";
  if (state) {
    documentNumber = state?.doc;
  }

  const updateHourCurrencyValue = async (horas_cod_referencia: number) => {
    let billedHoursIds = "";

    if (billedHours.length > 0) {
      billedHours.forEach((item: IBilledHours) => {
        if (billedHoursIds === "") {
          billedHoursIds = `(${item.horas_id})`;
        } else {
          billedHoursIds += `,(${item.horas_id})`;
        }
      });
    }

    const newValue = localStorage.getItem("@Jurisnet:referenceValue");
    let hourValue = 0;
    if (newValue) {
      hourValue = Number(newValue);
    }

    const formData = {
      horas_valor_hora: hourValue,
      doc: documentNumber,
      selectedHours: billedHoursIds,
    };

    let hourReference = 0;
    if (horas_cod_referencia !== undefined) {
      hourReference = horas_cod_referencia;
    }
    await api
      .patch(`/management/workinghours/patch/${hourReference}`, formData)
      .then(() => {
        // localStorage.removeItem("@Jurisnet:referenceValue");
        localStorage.setItem("@Jurisnet:hourreporttab", "1");
        history.go(0);
      });
  };

  const rowClass = (data: IBilledHours) => {
    return {
      "bg-bluegray-300": data.proc_cobrar_por_hora === "N",
    };
  };

  return (
    <Container>
      <Toast ref={toast} position="bottom-right" />

      <Sidebar
        className="p-sidebar-md"
        visible={visibleChangeClient}
        position="right"
        onHide={hideChangeClient}
      >
        <h4>Alterar empresa</h4>

        {changeClientHour ? (
          <div className="col-12 md:col-12">
            <table
              width="100%"
              id="changeclient_summary"
              cellPadding={0}
              cellSpacing={0}
            >
              <tbody>
                <tr>
                  <td width="140" style={{ padding: "5px" }}>
                    <strong>Data:</strong>
                  </td>
                  <td style={{ padding: "5px" }}>
                    {changeClientHour.hora_data ||
                      (changeClientHour.horas_data
                        ? moment(changeClientHour.horas_data).format(
                            "DD/MM/YYYY"
                          )
                        : "")}
                  </td>
                </tr>
                <tr style={{ background: "#e2e2e2" }}>
                  <td width="140" style={{ padding: "5px" }}>
                    <strong>Descrição:</strong>
                  </td>
                  <td style={{ padding: "5px" }}>
                    {changeClientHour.horas_descricao}
                  </td>
                </tr>
                <tr>
                  <td width="140" style={{ padding: "5px" }}>
                    <strong>Empresa atual:</strong>
                  </td>
                  <td style={{ padding: "5px" }}>
                    {changeClientHour.cliente_nome}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="col-12 md:col-12 p-mt-20">
              <label htmlFor="new_cliente">Nova empresa</label>
              <Dropdown
                id="new_cliente"
                name="new_cliente"
                options={customersOptions}
                value={newClientId}
                onChange={(e) => setNewClientId(e.value)}
                className="inputfield w-full"
                optionLabel="name"
                optionValue="value"
                filter
                resetFilterOnHide
                filterBy="name"
                placeholder="Selecione uma empresa"
                emptyFilterMessage="Nenhum registro encontrado"
              />
            </div>

            {selectedHours.length > 1 &&
            selectedHours.some(
              (item) => item.horas_id === changeClientHour.horas_id
            ) ? (
              <div className="col-12 md:col-12 p-mt-10 p-field-checkbox">
                <Checkbox
                  inputId="apply_to_all_selected"
                  checked={applyToAllSelected}
                  onChange={(e) => setApplyToAllSelected(e.checked ?? false)}
                />
                <label htmlFor="apply_to_all_selected" className="p-ml-2">
                  Aplicar a todos os {selectedHours.length} registros
                  selecionados
                </label>
              </div>
            ) : null}

            <div className="col-12 md:col-12 p-mt-20">
              <Button
                icon="pi pi-check"
                iconPos="left"
                label="Salvar"
                className="p-button-primary p-mr-2"
                type="button"
                onClick={saveChangeClient}
              />
              <Button
                icon="pi pi-times"
                iconPos="left"
                label="Cancelar"
                className="p-button-secondary"
                type="button"
                onClick={hideChangeClient}
              />
            </div>
          </div>
        ) : null}
      </Sidebar>

      <div className="datatable-responsive">
        <div className="p-toolbar p-component p-mb-20" role="toolbar">
          <div className="p-toolbar-group-left">
            <h3>Referência de valores</h3>
          </div>
        </div>
        <table
          key={nanoid()}
          width="500"
          className="referencevalues-table"
          id="referencevalues-table"
        >
          <thead>
            <tr key={nanoid()}>
              <th>Referência</th>
              <th>Valor (R$)</th>
            </tr>
          </thead>
          <tbody>
            {referenceValues.map((value) => (
              <tr
                key={nanoid()}
                className="py-2 px-2 border-top-1 border-300 flex-wrap"
              >
                <td width="250" className="text-500 w-full font-medium">
                  {value.horas_referencia_nome
                    ? value.horas_referencia_nome
                    : " "}
                </td>
                <td
                  width="80"
                  className="text-900 w-full md:flex-order-0 flex-order-1 line-height-3"
                >
                  <InputNumber
                    id="horas_valor_hora"
                    name="horas_valor_hora"
                    className="inputfield w-full"
                    value={referenceValue || value.horas_valor_hora}
                    onValueChange={(e) => {
                      if (e.value) {
                        setReferenceValue(e.value);
                        localStorage.setItem(
                          "@Jurisnet:referenceValue",
                          e.value.toString()
                        );
                      }
                    }}
                    placeholder="0,00"
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    locale="pt-BR"
                  />
                </td>
                <td width="40">
                  <Button
                    type="button"
                    icon="pi pi-check-circle"
                    className="p-button-rounded p-button-text p-button-plain btn-value-update"
                    onClick={() => {
                      updateHourCurrencyValue(value.horas_cod_referencia);
                    }}
                    tooltip="Atualizar valores de referência"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <hr />

        <div className="p-toolbar p-component p-mb-20" role="toolbar">
          <div className="p-toolbar-group-left">
            <h3>
              Relação de horas para o período selecionado ({initialSearchDate}{" "}
              até {finalSearchDate})
            </h3>
          </div>
          <div className="p-toolbar-group-right">
            <Button
              type="button"
              icon="pi pi-chevron-circle-down"
              onClick={() => {
                const element = document.querySelector("#table_summary");
                if (element) {
                  element.scrollIntoView();
                }
              }}
              label="Visualizar resumo horas"
              className="p-button-primary mr-2"
              disabled={loading}
            />
          </div>
        </div>

        <Tooltip target=".export-buttons>button" position="bottom" />
        <DataTable
          tableClassName="table-billedhours table"
          id="table-billedhours"
          ref={dt}
          loading={loading}
          value={billedHours}
          editMode="row"
          dataKey="horas_id"
          header={header}
          stripedRows
          rowClassName={rowClass}
          className="p-datatable-sm table-pendinghours"
          paginator
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords}"
          rows={billedHours.length}
          rowsPerPageOptions={[10, 20, 50, billedHours.length]}
          paginatorLeft={paginatorLeft}
          paginatorRight={paginatorRight}
          globalFilter={globalFilter}
          emptyMessage="Nenhum registro encontrado."
          responsiveLayout="scroll"
          selectionMode="checkbox"
          selection={selectedHours}
          onSelectionChange={(e) => {
            const selHours = e.value as IBilledHours[];
            setSelectedHours(selHours);
          }}
          footerColumnGroup={footerGroup}
        >
          <Column
            key="row_number"
            field="row_number"
            header="Núm."
            alignHeader="center"
            body={counterBodyTemplate}
            align="center"
            style={{ width: "50px" }}
          />
          <Column
            key="hora_data"
            field="hora_data"
            header="Data"
            alignHeader="center"
            align="left"
            style={{ width: "100px" }}
          />
          <Column
            field="advogado_nome"
            header="Responsável"
            alignHeader="center"
            body={personInChargeBodyTemplate}
            align="left"
            style={{ width: "140px" }}
          />
          <Column
            field="cliente_nome"
            header="Cliente"
            alignHeader="center"
            align="left"
            style={{ width: "140px" }}
          />
          <Column
            field="horas_referencia_nome"
            header="Referência"
            alignHeader="center"
            align="left"
            style={{ width: "80px" }}
          />
          <Column
            field="horas_descricao"
            header="Descrição"
            alignHeader="center"
            align="left"
          />
          <Column
            field="horas_tipo"
            header="Tipo"
            alignHeader="center"
            align="left"
            style={{ width: "100px" }}
          />
          <Column
            field="horas_hora_total"
            header="Horas"
            alignHeader="center"
            body={hourBodyTemplate}
            align="center"
            style={{ width: "80px" }}
          />
          <Column
            field="horas_decimal"
            header="Decimais"
            alignHeader="center"
            body={decimalBodyTemplate}
            align="center"
            style={{ width: "80px" }}
          />
          <Column
            field="cobranca"
            header="Cobrança"
            alignHeader="center"
            body={cobrancaBodyTemplate}
            align="center"
            style={{ width: "95px" }}
          />
          {adjustment.current.length > 0 && adjustment.current !== "0,00" ? (
            <Column
              field="valor_ajuste_nao_faturado"
              header="Ajustes Decimais"
              alignHeader="center"
              body={totalDecimalBodyTemplate}
              align="center"
              style={{ width: "80px" }}
            />
          ) : null}
          <Column
            key="horas_id"
            field="horas_id"
            header="Enviar Inf."
            alignHeader="center"
            headerClassName="checkflex"
            body={checkboxBodyTemplate}
            selectionMode="multiple"
            align="center"
            style={{ width: "90px" }}
          />
          <Column body={actionBodyTemplate} headerStyle={{ width: "250px" }} />
        </DataTable>

        <div className="grid summary-grid p-mt-10">
          <div className="col-12 summary-panel">
            <table id="table_summary">
              <tbody>
                <tr>
                  <th colSpan={2} className="stripedRows">
                    Resumo horas trabalhadas
                  </th>
                </tr>
                <tr>
                  <td width="240">Total de horas</td>
                  <td width="100" align="right">
                    {totalTime}
                  </td>
                </tr>
                <tr className="stripedRows">
                  <td width="240">Total de horas (decimais)</td>
                  <td width="100" align="right">
                    {totalDecimalTime}
                  </td>
                </tr>
                {adjustment.current.length > 0 &&
                adjustment.current !== "0,00" ? (
                  <>
                    <tr>
                      <td width="240">Ajuste (decimais)</td>
                      <td width="100" align="right">
                        {adjustment.current}
                      </td>
                    </tr>
                  </>
                ) : null}
                <tr
                  className={
                    adjustment.current.length > 0 &&
                    adjustment.current !== "0,00"
                      ? "stripedRows"
                      : ""
                  }
                >
                  <td width="240">Valor cobrado por hora</td>
                  <td width="100" align="right">
                    {valuePerHour}
                  </td>
                </tr>
                <tr
                  className={
                    adjustment.current.length > 0 &&
                    adjustment.current !== "0,00"
                      ? ""
                      : "stripedRows"
                  }
                >
                  <td width="240">Total do período (R$)</td>
                  <td width="100" align="right">
                    <span id="totalvalue">{totalValuePeriod}</span>
                  </td>
                </tr>
                <tr
                  className={
                    adjustment.current.length > 0 &&
                    adjustment.current !== "0,00"
                      ? "stripedRows"
                      : ""
                  }
                >
                  <td width="240">Ajuste (R$)</td>
                  <td width="100" align="right">
                    <InputNumber
                      id="adjustment_value"
                      name="adjustment_value"
                      className="inputfield input-adjustment"
                      value={adjustmentInputValue}
                      onValueChange={(e) => {
                        setAdjustmentInputValue(e.value || 0);
                        enteredAdjustmentValue.current = e.value || 0;
                      }}
                      /* onValueChange={(e) => {
                        const adjustmentVal =
                          typeof e.value === "number" ? e.value : 0;
                        enteredAdjustmentValue.current = adjustmentVal;
                        setAdjustmentInputValue(adjustmentVal);

                        const parsedTotalValue = Number.parseFloat(
                          totalValuePeriod.replaceAll(".", "").replace(",", ".")
                        );
                        const safeTotalValue = isNaN(parsedTotalValue)
                          ? 0
                          : parsedTotalValue;

                        const parsedAdjustment = Number.parseFloat(
                          (adjustment.current || "0").replace(",", ".")
                        );
                        const safeAdjustment = isNaN(parsedAdjustment)
                          ? 0
                          : parsedAdjustment;

                        const newTotalValue =
                          round(safeTotalValue, 2) +
                          adjustmentVal +
                          safeAdjustment *
                            Number(globalCategoryValue.current || 0);

                        const newTotalValueString =
                          newTotalValue.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });

                        setTotalAddedValuePeriod(newTotalValueString);
                      }} */
                      placeholder="0,00"
                      mode="decimal"
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      locale="pt-BR"
                      style={{ textAlign: "right" }}
                    />

                    {/* <InputNumber
                      id="adjustment_value"
                      name="adjustment_value"
                      className="inputfield input-adjustment"
                      value={enteredAdjustmentValue.current}
                      onValueChange={(e) => {
                        const adjustmentVal =
                          typeof e.value === "number" ? e.value : 0;
                        enteredAdjustmentValue.current = adjustmentVal;
                        setAdjustmentInputValue(enteredAdjustmentValue.current);

                        // Parse totalValuePeriod com segurança
                        const parsedTotalValue = Number.parseFloat(
                          totalValuePeriod.replaceAll(".", "").replace(",", ".")
                        );
                        const safeTotalValue = isNaN(parsedTotalValue)
                          ? 0
                          : parsedTotalValue;

                        // Parse adjustment atual com segurança
                        const parsedAdjustment = Number.parseFloat(
                          (adjustment.current || "0").replace(",", ".")
                        );
                        const safeAdjustment = isNaN(parsedAdjustment)
                          ? 0
                          : parsedAdjustment;

                        // Calcular novo total
                        const newTotalValue =
                          round(safeTotalValue, 2) +
                          adjustmentVal +
                          safeAdjustment *
                            Number(globalCategoryValue.current || 0);

                        const newTotalValueString =
                          newTotalValue.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });

                        setTotalAddedValuePeriod(newTotalValueString);
                      }}
                      placeholder="0,00"
                      mode="decimal"
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      locale="pt-BR"
                      style={{ textAlign: "right" }}
                    /> */}
                  </td>
                </tr>
                <tr
                  className={
                    adjustment.current.length > 0 &&
                    adjustment.current !== "0,00"
                      ? ""
                      : "stripedRows"
                  }
                >
                  <td width="240">Valor Final (R$)</td>
                  <td width="100" align="right">
                    {totalAddedValuePeriod}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="col-12 md:col-12 reducedPadding">
              <small id="adjustment-help" className="block">
                Digite o valor monetário para simular o ajuste e em{" "}
                <strong>Aplicar ajuste</strong> para salvar.
              </small>
            </div>
            <div className="col-12 md:col-12 reducedPadding">
              <Button
                icon="pi pi-upload"
                iconPos="left"
                label="Imprimir/Exportar Selecionados"
                className="p-button-info w-full"
                type="button"
                onClick={printSelectedHours}
              />
            </div>
            <div className="col-12 md:col-12 reducedPadding equal-spacing">
              <Button
                icon="pi pi-check"
                iconPos="left"
                label="Aplicar ajuste"
                className="p-button-secondary"
                type="button"
                onClick={applyAdjustmentValue}
              />
              <Button
                icon="pi pi-times"
                iconPos="left"
                label="Cancelar ajuste"
                className="p-button-secondary"
                type="button"
                onClick={cancelAdjustmentValue}
              />
            </div>

            <div className="col-12 md:col-12 reducedPadding">
              <Button
                icon="pi pi-money-bill"
                iconPos="left"
                label="Faturar horas"
                className="p-button-info w-full"
                type="button"
                onClick={invoiceHours}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <Dialog
          visible={!!confirmacaoAjuste}
          style={{ width: "460px" }}
          header={
            confirmacaoAjuste?.acao === "desfazer"
              ? "Remover o ajuste desta hora?"
              : "Reduzir a cobrança para 50%?"
          }
          modal
          footer={
            <>
              <Button
                type="button"
                label="Cancelar"
                icon="pi pi-times"
                className="p-button-text"
                disabled={ajusteEmCurso}
                onClick={() => setConfirmacaoAjuste(null)}
              />
              <Button
                type="button"
                label={
                  confirmacaoAjuste?.acao === "desfazer"
                    ? "Remover ajuste"
                    : "Reduzir cobrança"
                }
                icon="pi pi-check"
                className="p-button-text"
                loading={ajusteEmCurso}
                onClick={confirmarAjuste}
              />
            </>
          }
          onHide={() => setConfirmacaoAjuste(null)}
        >
          {confirmacaoAjuste ? (
            <div className="confirmation-content ajuste-confirmacao">
              <span>
                A cobrança desta hora passa de{" "}
                <b>
                  {minutesToHHMM(
                    Math.round(confirmacaoAjuste.horasCobrancaAtual * 60)
                  )}
                </b>{" "}
                para{" "}
                <b>
                  {minutesToHHMM(
                    Math.round(confirmacaoAjuste.horasCobrancaNova * 60)
                  )}
                </b>{" "}
                {`(${confirmacaoAjuste.horasCobrancaNova.toLocaleString(
                  "pt-BR",
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}\u00a0h no faturamento).`}
              </span>
              <span>
                A hora do advogado continua{" "}
                <b>{minutesToHHMM(confirmacaoAjuste.minutosLancados)}</b> na
                avaliação interna.
              </span>
              {confirmacaoAjuste.substituiAjusteManual ? (
                <span className="ajuste-aviso">
                  Esta hora já tem um ajuste informado à mão. Aplicar 50%
                  substitui o valor anterior.
                </span>
              ) : null}
            </div>
          ) : null}
        </Dialog>
      </div>

      <div>
        <Dialog
          visible={deleteHourDialog}
          style={{ width: "450px" }}
          header="Confirmação"
          modal
          footer={deleteHourFooter}
          onHide={hidedeleteHourDialog}
        >
          <div className="confirmation-content">
            <i
              className="pi pi-exclamation-triangle p-mr-3"
              style={{ fontSize: "2rem" }}
            />
            {billedHours && (
              <span>
                Você tem certeza que deseja excluir
                <br />
                <br />
                <b>{billedHour.horas_descricao}</b>?
              </span>
            )}
          </div>
        </Dialog>
      </div>
    </Container>
  );
};

export default HourManagementReport;
